import { useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Radio, ArrowLeft, Loader2, Mail, CheckCircle } from "lucide-react";
import { toast } from "sonner";

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [resetToken, setResetToken] = useState(null);
  const { requestPasswordReset } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await requestPasswordReset(email);
      setSent(true);
      // In development, show the reset token
      if (response.reset_token) {
        setResetToken(response.reset_token);
      }
      toast.success("Password reset instructions sent");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send reset instructions");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-8 bg-background" data-testid="forgot-password-page">
      <div className="w-full max-w-md space-y-8">
        <div className="flex items-center gap-2 justify-center">
          <div className="h-10 w-10 rounded-lg bg-accent flex items-center justify-center">
            <Radio className="h-5 w-5 text-white" />
          </div>
          <span className="font-bold text-xl tracking-tight">oRTB Exchange</span>
        </div>

        <Card>
          <CardHeader className="space-y-1">
            <CardTitle className="text-2xl font-bold tracking-tight">
              {sent ? "Check your email" : "Forgot password?"}
            </CardTitle>
            <CardDescription>
              {sent
                ? "We've sent you instructions to reset your password"
                : "Enter your email and we'll send you reset instructions"}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {sent ? (
              <div className="space-y-4">
                <div className="flex items-center justify-center py-6">
                  <div className="h-16 w-16 rounded-full bg-emerald-500/10 flex items-center justify-center">
                    <CheckCircle className="h-8 w-8 text-emerald-500" />
                  </div>
                </div>
                <p className="text-sm text-center text-muted-foreground">
                  If an account exists with {email}, you'll receive a password reset link.
                </p>
                
                {/* Development only - show reset token */}
                {resetToken && (
                  <div className="mt-4 p-4 rounded-lg bg-muted/50 border">
                    <p className="text-xs text-muted-foreground mb-2">Development: Reset Token</p>
                    <code className="text-xs font-mono break-all" data-testid="reset-token">{resetToken}</code>
                    <Link
                      to={`/reset-password?token=${resetToken}`}
                      className="mt-2 block text-sm text-accent hover:underline"
                      data-testid="reset-password-link"
                    >
                      Reset password now
                    </Link>
                  </div>
                )}

                <div className="flex flex-col gap-2 mt-4">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setSent(false);
                      setResetToken(null);
                    }}
                    data-testid="try-again-btn"
                  >
                    <Mail className="mr-2 h-4 w-4" />
                    Try different email
                  </Button>
                  <Link to="/login">
                    <Button variant="ghost" className="w-full">
                      <ArrowLeft className="mr-2 h-4 w-4" />
                      Back to login
                    </Button>
                  </Link>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="name@company.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    data-testid="email-input"
                  />
                </div>

                <Button
                  type="submit"
                  className="w-full"
                  disabled={loading}
                  data-testid="submit-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    "Send reset instructions"
                  )}
                </Button>

                <Link to="/login">
                  <Button variant="ghost" className="w-full">
                    <ArrowLeft className="mr-2 h-4 w-4" />
                    Back to login
                  </Button>
                </Link>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
